// ============================================================
// XMO.01 D#3 — Security Patterns Registry
//
// Configurable, versioned pattern set for prompt injection
// detection. No LLM in the security loop — pure pattern match.
//
// To add patterns: append to the relevant category array below.
// Bump PATTERN_VERSION on any change.
// All regexes are pre-compiled at module load — never construct
// inline regexes inside the scan loop.
//
// Spec: HLX-XMO.01, Section: Chat Injection
// ============================================================

export const PATTERN_VERSION = '1.0.0';

export type InjectionCategory =
  | 'injection_template'   // Known system-prompt override templates
  | 'jailbreak'            // DAN-style, mode-switch, bypass phrases
  | 'encoded_attack'       // Base64 blobs, unicode tricks, HTML entity abuse
  | 'social_engineering';  // Role-play, impersonation, identity override

export interface SecurityPattern {
  /** Unique pattern ID — logged on detection (message content is NOT logged) */
  id: string;
  category: InjectionCategory;
  /** Pre-compiled at module load — do NOT use inline /regex/ in scan loop */
  regex: RegExp;
  description: string;
}

// ─── Injection Templates ────────────────────────────────────
// Phrases that attempt to override, discard, or replace the system prompt.

const INJECTION_TEMPLATES: SecurityPattern[] = [
  {
    id: 'INJ-001',
    category: 'injection_template',
    regex: /ignore\s+(all\s+)?(previous|prior|above|earlier|your)\s+(instructions?|prompts?|directives?|rules?|constraints?)/i,
    description: 'Classic ignore-previous-instructions pattern',
  },
  {
    id: 'INJ-002',
    category: 'injection_template',
    regex: /disregard\s+(all\s+)?(previous|prior|your)\s+(instructions?|prompts?|training|rules?)/i,
    description: 'Disregard-instructions variant',
  },
  {
    id: 'INJ-003',
    category: 'injection_template',
    regex: /forget\s+(everything|all|your\s+(previous|prior|earlier)?\s*(instructions?|training|rules?|prompts?))/i,
    description: 'Forget-instructions variant',
  },
  {
    id: 'INJ-004',
    category: 'injection_template',
    regex: /\bnew\s+(instructions?|directives?|system\s*prompt|commands?|rules?)\s*:/i,
    description: 'Inline new-instructions injection header',
  },
  {
    id: 'INJ-005',
    category: 'injection_template',
    regex: /\bsystem\s*prompt\s*:/i,
    description: 'Explicit system prompt override attempt',
  },
  {
    id: 'INJ-006',
    category: 'injection_template',
    regex: /\[system\s*\]/i,
    description: 'Bracketed [SYSTEM] tag injection',
  },
  {
    id: 'INJ-007',
    category: 'injection_template',
    regex: /<\s*system\s*>/i,
    description: 'XML-style <system> tag injection',
  },
  {
    id: 'INJ-008',
    category: 'injection_template',
    regex: /override\s+(your\s+)?(safety|filter|instructions?|training|rules?|constraints?|guidelines?)/i,
    description: 'Direct safety/filter override instruction',
  },
  {
    id: 'INJ-009',
    category: 'injection_template',
    regex: /your\s+(real\s+)?(true\s+)?instructions?\s+(are|say|state)\s*[:\-]/i,
    description: 'Instruction-replacement "your real instructions are" pattern',
  },
  {
    id: 'INJ-010',
    category: 'injection_template',
    regex: /\bassistant\s*:\s*(?!i |my |yes|no|sure|okay|of course)/i,
    description: 'Injected assistant-role turn marker',
  },
];

// ─── Jailbreak Phrases ──────────────────────────────────────
// Mode-switch, capability-unlock, and filter-bypass phrases.

const JAILBREAK_PATTERNS: SecurityPattern[] = [
  {
    id: 'JB-001',
    category: 'jailbreak',
    regex: /\bDAN\b(?:\s+mode)?/,
    description: '"Do Anything Now" jailbreak name',
  },
  {
    id: 'JB-002',
    category: 'jailbreak',
    regex: /do\s+anything\s+now/i,
    description: 'DAN expanded phrase',
  },
  {
    id: 'JB-003',
    category: 'jailbreak',
    regex: /\bjailbreak\b/i,
    description: 'Explicit jailbreak keyword',
  },
  {
    id: 'JB-004',
    category: 'jailbreak',
    regex: /developer\s+mode/i,
    description: 'Developer mode activation phrase',
  },
  {
    id: 'JB-005',
    category: 'jailbreak',
    regex: /\bgod\s+mode\b/i,
    description: 'God mode unlock phrase',
  },
  {
    id: 'JB-006',
    category: 'jailbreak',
    regex: /unlimited\s+mode/i,
    description: 'Unlimited mode unlock phrase',
  },
  {
    id: 'JB-007',
    category: 'jailbreak',
    regex: /sudo\s+mode|sudo\s*:/i,
    description: 'Sudo-style privilege escalation',
  },
  {
    id: 'JB-008',
    category: 'jailbreak',
    regex: /bypass\s+(your\s+)?(filters?|safety|restrictions?|guidelines?|rules?|limits?)/i,
    description: 'Explicit filter/safety bypass instruction',
  },
  {
    id: 'JB-009',
    category: 'jailbreak',
    regex: /no\s+(restrictions?|limits?|filters?|rules?|guidelines?)\s+apply/i,
    description: '"No restrictions apply" unlock phrase',
  },
  {
    id: 'JB-010',
    category: 'jailbreak',
    regex: /you\s+(are\s+)?(now\s+)?(free\s+from|unrestricted|unfiltered|without\s+limits?)/i,
    description: '"You are now free/unrestricted" unlock phrase',
  },
  {
    id: 'JB-011',
    category: 'jailbreak',
    regex: /training\s+data\s+(doesn'?t?|does\s+not)\s+(apply|matter|restrict)/i,
    description: 'Training-override phrase',
  },
  {
    id: 'JB-012',
    category: 'jailbreak',
    regex: /\benable\s+(all\s+)?capabilities\b/i,
    description: 'Capability-unlock phrase',
  },
];

// ─── Encoded Attacks ────────────────────────────────────────
// Base64 blobs, unicode homoglyph/zero-width tricks, HTML entity obfuscation.

const ENCODED_ATTACK_PATTERNS: SecurityPattern[] = [
  {
    id: 'ENC-001',
    category: 'encoded_attack',
    // Suspicious base64: 40+ chars of base64 alphabet, possibly with = padding.
    // Legitimate patient messages rarely contain long base64 blobs.
    regex: /[A-Za-z0-9+/]{40,}={0,2}/,
    description: 'Suspicious base64 blob (>=40 chars)',
  },
  {
    id: 'ENC-002',
    category: 'encoded_attack',
    // Zero-width characters used to hide injected text
    regex: /[\u200b\u200c\u200d\u200e\u200f\u2060\u2061\u2062\u2063\ufeff]/,
    description: 'Zero-width / invisible Unicode characters',
  },
  {
    id: 'ENC-003',
    category: 'encoded_attack',
    // HTML numeric entity encoding (&#xx; or &#xxx;) used to obfuscate injection text
    regex: /&#\d{2,4};|&#x[0-9a-fA-F]{2,4};/,
    description: 'HTML numeric entity encoding',
  },
  {
    id: 'ENC-004',
    category: 'encoded_attack',
    // Stacked HTML named entities suggesting obfuscation (e.g., &amp;lt;script)
    regex: /(&amp;){2,}|&amp;[lg]t;/i,
    description: 'Stacked/double-encoded HTML named entities',
  },
  {
    id: 'ENC-005',
    category: 'encoded_attack',
    // Cyrillic homoglyphs mixed into Latin text (common for Unicode bypass)
    // Checks for Cyrillic chars (\u0400-\u04FF) mixed with ASCII in same word
    regex: /[a-zA-Z][\u0400-\u04ff]|[\u0400-\u04ff][a-zA-Z]/,
    description: 'Cyrillic homoglyph characters mixed with Latin text',
  },
  {
    id: 'ENC-006',
    category: 'encoded_attack',
    // URL percent-encoding used outside a URL context to obfuscate injection
    regex: /(%[0-9a-fA-F]{2}){4,}/,
    description: 'Suspicious URL percent-encoding sequence',
  },
  {
    id: 'ENC-007',
    category: 'encoded_attack',
    // Detect "base64:" or "encoded:" prefixes signaling an encoded payload
    regex: /\b(?:base64|rot13|hex|encoded?|cipher)\s*:/i,
    description: 'Explicit encoding scheme prefix',
  },
];

// ─── Social Engineering ─────────────────────────────────────
// Role-play, identity override, impersonation attempts.

const SOCIAL_ENGINEERING_PATTERNS: SecurityPattern[] = [
  {
    id: 'SE-001',
    category: 'social_engineering',
    regex: /\bpretend\s+(you\s+are|to\s+be)\b/i,
    description: '"Pretend you are" identity override',
  },
  {
    id: 'SE-002',
    category: 'social_engineering',
    regex: /\bact\s+as\s+(a|an|the)\b/i,
    description: '"Act as a/an/the" role assignment',
  },
  {
    id: 'SE-003',
    category: 'social_engineering',
    regex: /\broleplay\s+as\b|\brole\s*-\s*play\s+as\b/i,
    description: '"Roleplay as" identity override',
  },
  {
    id: 'SE-004',
    category: 'social_engineering',
    regex: /you\s+are\s+now\s+(a|an|the)\b/i,
    description: '"You are now a/an/the" identity reset',
  },
  {
    id: 'SE-005',
    category: 'social_engineering',
    regex: /from\s+now\s+on\s+(you\s+are|you're|your\s+name)/i,
    description: '"From now on you are" persistent identity override',
  },
  {
    id: 'SE-006',
    category: 'social_engineering',
    regex: /forget\s+you\s+are\s+(a|an|the|trained|designed)/i,
    description: '"Forget you are" identity suppression',
  },
  {
    id: 'SE-007',
    category: 'social_engineering',
    regex: /\bimpersonate\b/i,
    description: 'Explicit impersonation instruction',
  },
  {
    id: 'SE-008',
    category: 'social_engineering',
    regex: /simulate\s+(being|a|an|the)\b/i,
    description: '"Simulate being/a" identity override',
  },
  {
    id: 'SE-009',
    category: 'social_engineering',
    regex: /your\s+(true\s+)?personality\s+is\b/i,
    description: '"Your true personality is" identity manipulation',
  },
  {
    id: 'SE-010',
    category: 'social_engineering',
    regex: /you\s+(were\s+)?programmed\s+(to|by)\b/i,
    description: '"You were programmed to/by" authority spoof',
  },
];

/**
 * The full versioned pattern set.
 * All regexes are pre-compiled at module load — no per-call regex construction.
 */
export const SECURITY_PATTERNS: readonly SecurityPattern[] = [
  ...INJECTION_TEMPLATES,
  ...JAILBREAK_PATTERNS,
  ...ENCODED_ATTACK_PATTERNS,
  ...SOCIAL_ENGINEERING_PATTERNS,
];

/** Total pattern count — for diagnostics */
export const PATTERN_COUNT = SECURITY_PATTERNS.length;
